/**
 * 实验三级级联筛选器组件（内部使用单个 ABTestCascader，对外暴露 IExperimentCascadeValue）
 * 默认无筛选器只显示添加按钮；添加后仅有一个筛选器，添加按钮隐藏；无数据时删除移除筛选器，有数据时清空选项
 */
import React, { useCallback } from 'react';
import classNames from 'classnames';
import { Bubble } from 'tea-component';
import { useIntl } from 'umi';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { useDblIntl } from '@/pages/datalab/utils/utils';
import { IExperimentCascadeProps } from './types';
import ABTestCascader from '../ABTestCascader';
import AddButton from '../AddButton';
import './index.scss';

const ExperimentCascade: React.FC<IExperimentCascadeProps> = (props) => {
  const { className, title, required, value, experimentFilters = [], onChange } = props;
  const { formatMessage } = useIntl();
  const { commonFormatMessage } = useDblIntl('common');

  const cascaderValue = (Array.isArray(value) ? value : []) as string[][];
  const hasFilter = cascaderValue?.length > 0;
  const hasData = cascaderValue?.some(item => item?.length > 0);
  const handleAddFilter = useCallback(() => {
    onChange?.([[]]);
  }, [onChange]);

  const handleChange = useCallback(
    (paths: string[][]) => {
      // 当用户在级联内清空所有选中时，保留筛选器槽位，避免整块被卸载
      if (paths.length === 0 && hasFilter) {
        onChange?.([[]]);
      } else {
        onChange?.(paths);
      }
    },
    [onChange, hasFilter]
  );

  const handleDeleteOrClear = useCallback(() => {
    if (hasData) {
      onChange?.([[]]);
    } else {
      onChange?.([]);
    }
  }, [hasData, onChange]);

  const deleteTooltip = hasData
    ? commonFormatMessage('clear')
    : formatMessage({ id: 'dbl_ab_experiment_experiment_cascade_remove_filter' });

  return (
    <div className={classNames('dbl-experiment-cascade', className)}>
      {title && (
        <div className={classNames('dbl-experiment-cascade-title', { 'is-required': required })}>
          {title}
        </div>
      )}
      <div className="dbl-experiment-cascade-list">
        {hasFilter && (
          <div className="dbl-experiment-cascade-row">
            <div className="dbl-experiment-cascade-row-wrap">
              <div className="dbl-experiment-cascade-cascader-wrap">
                <ABTestCascader
                  data={experimentFilters}
                  value={cascaderValue?.filter(item => item?.length > 0)}
                  onChange={handleChange}
                />
              </div>
              <Bubble
                dark={true}
                arrowPointAtCenter
                placement="top"
                content={<div><p>{deleteTooltip}</p></div>}
              >
                <i
                  className="dbl-experiment-cascade-delete"
                  onClick={handleDeleteOrClear}
                  role="button"
                  aria-label={hasData ? 'clear' : 'remove'}
                >
                  <SvgIcon type="clonse" />
                </i>
              </Bubble>
            </div>
          </div>
        )}
        {!hasFilter && (
          <AddButton
            className="dbl-experiment-cascade-add-button"
            text={formatMessage({ id: 'dbl_ab_experiment_experiment_cascade_add_button' })}
            onClick={handleAddFilter}
          />
        )}
      </div>
    </div>
  );
};

export default ExperimentCascade;
